import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeTypePolicy } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { INTERNAL_SUBTASKS_DEFAULT_OFF_CODES } from '../changeTypePolicy';

const CODE_HINT = 'Letras minúsculas, números e _ (ex.: normal_correcao).';

const ChangeTypePoliciesPanel: React.FC = () => {
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const canManage = isChangeManager(user);
  const [items, setItems] = useState<ChangeTypePolicy[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [draftCode, setDraftCode] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftInternal, setDraftInternal] = useState(false);
  const [draftExternal, setDraftExternal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await changesApi.listTypePolicies({ only_active: false });
      setItems(data.items || []);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar os tipos de mudança.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patchRow = (id: number, patch: Partial<ChangeTypePolicy>) => {
    setItems((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const saveRow = async (row: ChangeTypePolicy) => {
    if (!canManage) return;
    setSavingId(row.id);
    try {
      const saved = await changesApi.updateTypePolicy(row.id, {
        name: row.name,
        description: row.description ?? null,
        enable_internal_subtasks: row.enable_internal_subtasks,
        enable_external_subtasks: row.enable_external_subtasks,
        is_active: row.is_active,
      });
      setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar este tipo.'));
      void load();
    } finally {
      setSavingId(null);
    }
  };

  const handleToggle = async (
    row: ChangeTypePolicy,
    field: 'enable_internal_subtasks' | 'enable_external_subtasks' | 'is_active',
    next: boolean,
  ) => {
    const updated = { ...row, [field]: next };
    patchRow(row.id, { [field]: next });
    await saveRow(updated);
  };

  const handleCreate = async () => {
    if (!canManage) return;
    const code = draftCode.trim().toLowerCase();
    const name = draftName.trim();
    if (!code || !name) {
      showAlert({
        title: 'Campos obrigatórios',
        message: 'Preencha o código e o nome do novo tipo.',
      });
      return;
    }
    const ok = await showConfirm({
      title: 'Criar tipo de mudança',
      message: `Confirma o tipo "${name}" (${code})? Subtarefas internas e externas começam conforme os switches abaixo.`,
      confirmLabel: 'Criar',
    });
    if (!ok) return;
    setCreating(true);
    try {
      const created = await changesApi.createTypePolicy({
        code,
        name,
        description: draftDescription.trim() || null,
        enable_internal_subtasks: draftInternal,
        enable_external_subtasks: draftExternal,
        is_active: true,
      });
      setItems((prev) => [...prev, created].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setDraftCode('');
      setDraftName('');
      setDraftDescription('');
      setDraftInternal(false);
      setDraftExternal(false);
      setShowCreate(false);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar o tipo.'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <p className="text-muted mb-0 d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" aria-hidden />
        Carregando tipos de mudança…
      </p>
    );
  }

  return (
    <div>
      <Alert variant="light" className="border mb-3">
        <strong>Para que serve:</strong> escolher, para cada <em>tipo de mudança</em>, se ao criar
        ou acompanhar a mudança o sistema pode usar{' '}
        <strong>subtarefas internas</strong> (checklists por área, dentro do SupportNect) e/ou{' '}
        <strong>subtarefas externas</strong> (chamados no módulo de Incidentes).
        <ul className="mb-0 mt-2 small">
          <li>
            <strong>Internas</strong> — passo &quot;Confirme as subtarefas&quot;, aba Subtarefas →
            Novo; não abre ticket fora do SupportNect.
          </li>
          <li>
            <strong>Externas</strong> — chamados SI/Infra na criação ou botão &quot;Abrir chamado no
            Incidente&quot;.
          </li>
          <li>
            <strong>Ativo</strong> — se desligado, o tipo some do formulário Nova mudança.
          </li>
        </ul>
        <p className="mb-0 mt-2 small text-muted">
          Correção e Emergencial vêm com <strong>internas desligadas</strong> por padrão (só
          chamados), mas você pode ligar internas se a operação mudar. Tipos customizados começam
          com internas e externas desligadas até você ativar.
        </p>
      </Alert>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <div className="table-responsive mb-4">
        <Table hover className="align-middle mb-0">
          <thead>
            <tr>
              <th>Tipo</th>
              <th className="text-center">
                Internas
                <div className="fw-normal small text-muted">Checklists no SupportNect</div>
              </th>
              <th className="text-center">
                Externas
                <div className="fw-normal small text-muted">Chamados no Incidente</div>
              </th>
              <th className="text-center">Ativo</th>
              <th>Nome exibido</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="fw-semibold">{row.name}</div>
                  <Badge bg="secondary" className="font-monospace">
                    {row.code}
                  </Badge>
                  {row.is_system ? (
                    <Badge bg="light" text="dark" className="ms-1">
                      Sistema
                    </Badge>
                  ) : null}
                </td>
                <td className="text-center">
                  <Form.Check
                    type="switch"
                    id={`type-internal-${row.id}`}
                    aria-label={`Subtarefas internas para ${row.name}`}
                    checked={row.enable_internal_subtasks}
                    disabled={!canManage || savingId === row.id}
                    onChange={(e) =>
                      void handleToggle(row, 'enable_internal_subtasks', e.target.checked)
                    }
                  />
                  {INTERNAL_SUBTASKS_DEFAULT_OFF_CODES.has(row.code) &&
                  !row.enable_internal_subtasks ? (
                    <div className="small text-muted mt-1">Padrão: desligado</div>
                  ) : null}
                </td>
                <td className="text-center">
                  <Form.Check
                    type="switch"
                    id={`type-external-${row.id}`}
                    aria-label={`Subtarefas externas para ${row.name}`}
                    checked={row.enable_external_subtasks}
                    disabled={!canManage || savingId === row.id}
                    onChange={(e) =>
                      void handleToggle(row, 'enable_external_subtasks', e.target.checked)
                    }
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="switch"
                    id={`type-active-${row.id}`}
                    aria-label={`Tipo ${row.name} ativo`}
                    checked={row.is_active !== false}
                    disabled={!canManage || savingId === row.id || row.is_system}
                    onChange={(e) => void handleToggle(row, 'is_active', e.target.checked)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    aria-label={`Nome exibido de ${row.code}`}
                    value={row.name}
                    disabled={!canManage || savingId === row.id}
                    onChange={(e) => patchRow(row.id, { name: e.target.value })}
                    onBlur={() => {
                      if (canManage && row.name.trim().length >= 2) void saveRow(row);
                    }}
                  />
                  {row.description ? (
                    <div className="small text-muted mt-1">{row.description}</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {canManage ? (
        <Card className="border">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Novo tipo</span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setShowCreate((prev) => !prev)}
            >
              {showCreate ? 'Ocultar' : 'Adicionar tipo'}
            </Button>
          </Card.Header>
          {showCreate ? (
            <Card.Body>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Label htmlFor="new-type-code">Código</Form.Label>
                  <Form.Control
                    id="new-type-code"
                    value={draftCode}
                    placeholder="normal_correcao"
                    onChange={(e) => setDraftCode(e.target.value)}
                  />
                  <Form.Text className="text-muted">{CODE_HINT}</Form.Text>
                </Col>
                <Col md={8}>
                  <Form.Label htmlFor="new-type-name">Nome na interface</Form.Label>
                  <Form.Control
                    id="new-type-name"
                    value={draftName}
                    placeholder="Mudança Normal (Correção)"
                    onChange={(e) => setDraftName(e.target.value)}
                  />
                </Col>
                <Col md={12}>
                  <Form.Label htmlFor="new-type-desc">Descrição (opcional)</Form.Label>
                  <Form.Control
                    id="new-type-desc"
                    as="textarea"
                    rows={2}
                    value={draftDescription}
                    onChange={(e) => setDraftDescription(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="new-type-internal"
                    label="Permitir subtarefas internas"
                    checked={draftInternal}
                    onChange={(e) => setDraftInternal(e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="new-type-external"
                    label="Permitir subtarefas externas (Incidente)"
                    checked={draftExternal}
                    onChange={(e) => setDraftExternal(e.target.checked)}
                  />
                </Col>
              </Row>
              <Button className="mt-3" disabled={creating} onClick={() => void handleCreate()}>
                {creating ? 'Criando…' : 'Criar tipo'}
              </Button>
            </Card.Body>
          ) : (
            <Card.Body className="text-muted small mb-0">
              Use para tipos customizados. Por padrão, internas e externas ficam desligadas até você
              ligar.
            </Card.Body>
          )}
        </Card>
      ) : (
        <p className="text-muted small mb-0">Somente gestor de mudanças altera este cadastro.</p>
      )}
    </div>
  );
};

export default ChangeTypePoliciesPanel;
